import type { VoteState, VoteEntry } from '@/types'

/**
 * Manages barrage vote counting.
 * Accepts raw numbers and maintains a sorted top-3 list.
 */
export class VoteManager {
  static createState(): VoteState {
    return { counts: new Map(), top3: [] }
  }

  /** Record a vote for a number. Returns updated top3. */
  static addVote(state: VoteState, num: number): VoteEntry[] {
    const cur = state.counts.get(num) ?? 0
    state.counts.set(num, cur + 1)
    state.top3 = VoteManager.computeTop3(state)
    return state.top3
  }

  /** Remove votes for numbers outside [min, max] (range narrowed) */
  static pruneOutOfRange(state: VoteState, min: number, max: number): void {
    for (const key of state.counts.keys()) {
      if (key < min || key > max) state.counts.delete(key)
    }
    state.top3 = VoteManager.computeTop3(state)
  }

  /** Reset all vote data */
  static reset(state: VoteState): void {
    state.counts.clear()
    state.top3 = []
  }

  private static computeTop3(state: VoteState): VoteEntry[] {
    return Array.from(state.counts.entries())
      .map(([number, count]) => ({ number, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 3)
  }
}
